/**
 * General button component.
 * Pass in the type, disabled if needed, and set secondary or primary colour (default is type=button primary)
 */

import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import Link from 'next/link';
import React, { type ComponentPropsWithoutRef, type ReactElement } from 'react';

export const ButtonType = {
  button: 'button',
  submit: 'submit',
} as const;

export const BUTTON_VARIANT_STYLES = {
  /* 
    flex stuff is for when there are icons 
    outline and focus ring for accessibility
  */
  primary:
    'flex items-center h-10 justify-center gap-1 outline-none focus:ring-2 rounded-full bg-orange text-white py-2 px-6 uppercase text-base font-bold font-primary text-center border-orange border hover:bg-black hover:border-white active:bg-black active:border-white disabled:bg-grey-dark disabled:border-grey-dark disabled:border disabled:font-normal dark:disabled:bg-grey-darkest dark:disabled:border-grey-light dark:disabled:text-grey-mid disabled:cursor-not-allowed',
  secondary:
    'flex items-center h-10 justify-center gap-1 outline-none focus:ring-2 rounded-full bg-black border border-white text-white py-2 px-6 uppercase text-base font-bold font-primary text-center hover:bg-orange active:bg-orange disabled:bg-grey-dark disabled:border-grey-dark disabled:border disabled:font-normal dark:disabled:bg-grey-darkest dark:disabled:border-grey-light dark:disabled:text-grey-mid disabled:cursor-not-allowed',
  tertiary:
    'flex items-center h-10 justify-center gap-1 outline-none rounded-full bg-transparent uppercase text-base font-bold font-primary text-center dark:disabled:text-grey-mid disabled:font-normal disabled:cursor-not-allowed',
  text: 'clickable',
} as const;

interface BaseButtonProps {
  icon?: React.ReactNode;
  children?: React.ReactNode;
  buttonStyle?: keyof typeof BUTTON_VARIANT_STYLES;
  testid?: string;
  className?: string;
  scrollToId?: string;
  href?: string;
}

export interface ButtonProps
  extends BaseButtonProps,
    ComponentPropsWithoutRef<'button'> {}

export interface CustomLinkProps
  extends BaseButtonProps,
    ComponentPropsWithoutRef<'a'> {}

// Button component is wrapped with forwardRef (mainly for tooltips)
// need to pass the forwardRef our typings so it knows what the ref is for
export const Button = React.forwardRef<HTMLElement, ButtonProps>(
  (
    {
      icon,
      children,
      buttonStyle = 'primary',
      onClick,
      testid,
      className,
      scrollToId,
      ...props
    },
    forwardedRef
  ): ReactElement => {
    // Getting rid of base styles, it confuses things. Each style defines all it will do
    const buttonTypeStyle = BUTTON_VARIANT_STYLES[buttonStyle];

    // Check for an icon and no children, always include a px in this case
    const classesToApply =
      icon && !children
        ? mergeStyles('px-1', buttonTypeStyle)
        : buttonTypeStyle;

    // Return a normal anchor tag or Link component if href is passed
    // unfortunately TS is too dumb to split out typings here, so cast the props passed down as CustomLinkProps
    if ('href' in props && typeof props.href === 'string') {
      // If here we can cast the props as CustomLink
      const linkProps = { ...props } as CustomLinkProps;

      if (props.href === '/rewards' || props.href === '/checkout') {
        return (
          <a
            {...linkProps}
            href={props.href}
            className={mergeStyles(className ?? '', classesToApply)}
            ref={forwardedRef as undefined | React.Ref<HTMLAnchorElement>}
            data-umami-event="hue-link-click"
            data-umami-event-slug={props.href}
          >
            {icon} {children}
          </a>
        );
      }
      // Return a normal Link component for every other href than rewards
      return (
        <Link
          {...linkProps}
          prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
          href={props.href}
          className={mergeStyles(className ?? '', classesToApply)}
          ref={forwardedRef as undefined | React.Ref<HTMLAnchorElement>}
          data-umami-event="hue-link-click"
          data-umami-event-slug={props.href}
        >
          {icon} {children}
        </Link>
      );
    }

    // It is a typical button
    const onClickAction = scrollToId
      ? () => document.getElementById(scrollToId)?.scrollIntoView()
      : (onClick as React.MouseEventHandler<HTMLButtonElement> | undefined);

    // Default the button type to 'button' if not set, plus annoying ts type casting
    const explicitButtonType =
      'type' in props
        ? (props.type as React.ButtonHTMLAttributes<HTMLButtonElement>['type'])
        : 'button';

    return (
      <button
        {...props}
        type={explicitButtonType}
        onClick={onClickAction}
        data-testid={testid}
        className={mergeStyles(className ?? '', classesToApply)}
        ref={forwardedRef as undefined | React.Ref<HTMLButtonElement>}
        data-umami-event="hue-button-click"
      >
        {icon} {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
